/**
 * Odds API Client for fetching odds with deep links
 * Direct client-side integration with The Odds API
 */

// Using the existing ODDS_API_KEY environment variable
// Note: React requires REACT_APP_ prefix for environment variables
const ODDS_API_KEY = process.env.REACT_APP_ODDS_API_KEY;
const BASE_URL = 'https://api.the-odds-api.com/v4';

// Supported bookmakers with their display names and colors
export const BOOKMAKERS = {
  fanduel: { 
    name: 'FanDuel', 
    color: 'from-blue-600 to-blue-500',
    textColor: 'text-white',
    homepage: 'https://sportsbook.fanduel.com'
  },
  draftkings: { 
    name: 'DraftKings', 
    color: 'from-green-600 to-green-500',
    textColor: 'text-white',
    homepage: 'https://sportsbook.draftkings.com'
  },
  betmgm: { 
    name: 'BetMGM', 
    color: 'from-yellow-600 to-yellow-500',
    textColor: 'text-white',
    homepage: 'https://sports.betmgm.com'
  },
  caesars: { 
    name: 'Caesars', 
    color: 'from-purple-600 to-purple-500',
    textColor: 'text-white',
    homepage: 'https://caesars.com/sportsbook'
  },
  pointsbetus: { 
    name: 'PointsBet', 
    color: 'from-red-600 to-red-500',
    textColor: 'text-white',
    homepage: 'https://sportsbook.pointsbet.com'
  },
  williamhill_us: { 
    name: 'William Hill', 
    color: 'from-indigo-600 to-indigo-500',
    textColor: 'text-white',
    homepage: 'https://www.williamhill.com/us'
  },
  wynnbet: { 
    name: 'WynnBET', 
    color: 'from-orange-600 to-orange-500',
    textColor: 'text-white',
    homepage: 'https://www.wynnbet.com'
  },
  unibet_us: { 
    name: 'Unibet', 
    color: 'from-green-700 to-green-600',
    textColor: 'text-white',
    homepage: 'https://sportsbook.unibet.com'
  },
  betrivers: { 
    name: 'BetRivers', 
    color: 'from-blue-700 to-blue-600',
    textColor: 'text-white',
    homepage: 'https://betrivers.com'
  }
};

/**
 * Fetch odds with deep links for a specific sport
 * @param {string} sport - Sport key (e.g., 'americanfootball_nfl', 'basketball_nba')
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Odds data with deep links
 */
export async function fetchOddsWithLinks(sport, options = {}) {
  const {
    bookmakers = Object.keys(BOOKMAKERS),
    markets = 'h2h,spreads,totals',
    oddsFormat = 'american',
    regions = 'us'
  } = options;

  if (!ODDS_API_KEY) {
    console.warn('REACT_APP_ODDS_API_KEY is not set. Please add it to your .env file.');
    throw new Error('Odds API key is not configured');
  }

  // Filter out player props markets - these require eventId and cannot be fetched at sport level
  const playerPropsMarkets = ['player_points', 'player_rebounds', 'player_assists', 'player_pass_tds',
                               'player_rush_yds', 'player_receptions', 'player_reception_yds'];

  const marketsList = markets.split(',').map(m => m.trim());
  const validMarkets = marketsList.filter(m => !playerPropsMarkets.includes(m));
  const removedMarkets = marketsList.filter(m => playerPropsMarkets.includes(m));

  if (removedMarkets.length > 0) {
    console.warn('⚠️ Removed player props markets from request (require eventId):', removedMarkets);
    console.warn('ℹ️ To fetch player props: First get event IDs, then query player props per event');
  }

  if (validMarkets.length === 0) {
    throw new Error('No valid markets selected. Player props markets (player_points, player_rebounds, etc.) require an eventId and cannot be fetched at the sport level. Please select at least one game-level market (h2h, spreads, totals).');
  }

  const filteredMarkets = validMarkets.join(',');

  const params = new URLSearchParams({
    apiKey: ODDS_API_KEY,
    bookmakers: bookmakers.join(','),
    markets: filteredMarkets,
    oddsFormat,
    regions,
    includeLinks: 'true',
    includeSids: 'true'
  });

  try {
    const response = await fetch(`${BASE_URL}/sports/${sport}/odds?${params}`);

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return processOddsData(data);
  } catch (error) {
    console.error('Error fetching odds with links:', error);
    throw error;
  }
}

/**
 * Process odds data and enhance with link information
 */
function processOddsData(rawData) {
  return rawData.map(event => {
    const processedEvent = {
      id: event.id,
      sport_key: event.sport_key,
      sport_title: event.sport_title,
      commence_time: event.commence_time,
      home_team: event.home_team,
      away_team: event.away_team,
      bookmakers: {}
    };

    // Process each bookmaker's data
    event.bookmakers?.forEach(bookmaker => {
      const bookmakerData = {
        key: bookmaker.key,
        title: bookmaker.title,
        last_update: bookmaker.last_update,
        event_link: bookmaker.link,
        event_sid: bookmaker.sid,
        markets: {}
      };

      // Process each market
      bookmaker.markets?.forEach(market => {
        const marketData = {
          key: market.key,
          link: market.link,
          sid: market.sid,
          outcomes: {}
        };

        // Process each outcome
        market.outcomes?.forEach(outcome => {
          // For player props, use "description - name" as key (e.g., "LeBron James - Over")
          // For game odds, use just name (e.g., "Miami Heat")
          const outcomeKey = outcome.description
            ? `${outcome.description} - ${outcome.name}`
            : outcome.name;

          marketData.outcomes[outcomeKey] = {
            name: outcome.name, // Over/Under or team name
            description: outcome.description, // Player name (for props)
            price: outcome.price,
            point: outcome.point,
            link: outcome.link,
            sid: outcome.sid,
            // Determine best available link
            best_link: outcome.link || market.link || bookmaker.link
          };
        });

        bookmakerData.markets[market.key] = marketData;
      });

      processedEvent.bookmakers[bookmaker.key] = bookmakerData;
    });

    return processedEvent;
  });
}

/**
 * Extract betting information from a text selection
 * @param {string} betText - The text containing bet information
 * @returns {Object} Parsed bet information
 */
export function parseBetText(betText) {
  // This is a simple parser - you may need to make it more sophisticated
  const parsed = {
    team: null,
    type: null, // 'moneyline', 'spread', 'total'
    value: null,
    game: null
  };

  // Check for moneyline
  if (betText.match(/\bML\b|moneyline|to win/i)) {
    parsed.type = 'moneyline';
  }
  // Check for spread
  else if (betText.match(/[+-]\d+\.?\d*|spread|points/i)) {
    parsed.type = 'spread';
    const spreadMatch = betText.match(/([+-]\d+\.?\d*)/);
    if (spreadMatch) parsed.value = spreadMatch[1];
  }
  // Check for totals
  else if (betText.match(/over|under|O\/U|total/i)) {
    parsed.type = 'total';
    const totalMatch = betText.match(/\d+\.?\d*/);
    if (totalMatch) parsed.value = totalMatch[0];
  }

  // Extract team name (simplified - enhance as needed)
  const teams = betText.match(/([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/g);
  if (teams && teams.length > 0) {
    parsed.team = teams[0];
  }

  return parsed;
}

/**
 * Find matching odds for a parsed bet
 * @param {Object} parsedBet - Parsed bet information
 * @param {Array} oddsData - Processed odds data
 * @returns {Object} Matching odds with links
 */
export function findMatchingOdds(parsedBet, oddsData) {
  const matches = {};

  oddsData.forEach(event => {
    // Check if this event matches the bet
    const isTeamMatch = 
      event.home_team.includes(parsedBet.team) || 
      event.away_team.includes(parsedBet.team) ||
      parsedBet.team?.includes(event.home_team) ||
      parsedBet.team?.includes(event.away_team);

    if (isTeamMatch || !parsedBet.team) {
      Object.entries(event.bookmakers).forEach(([bookmakerKey, bookmaker]) => {
        // Determine which market to look at
        let marketKey = 'h2h'; // Default to moneyline
        if (parsedBet.type === 'spread') marketKey = 'spreads';
        if (parsedBet.type === 'total') marketKey = 'totals';

        const market = bookmaker.markets[marketKey];
        if (market) {
          // Find the relevant outcome
          let outcome;
          if (parsedBet.type === 'moneyline') {
            outcome = market.outcomes[parsedBet.team] || 
                     market.outcomes[event.home_team] ||
                     market.outcomes[event.away_team];
          } else if (parsedBet.type === 'spread') {
            // For spreads, match team and point value
            Object.entries(market.outcomes).forEach(([name, data]) => {
              if (name.includes(parsedBet.team) || parsedBet.team?.includes(name)) {
                if (!parsedBet.value || data.point == parsedBet.value) {
                  outcome = data;
                }
              }
            });
          } else if (parsedBet.type === 'total') {
            // For totals, match over/under
            const isOver = parsedBet.team?.match(/over/i);
            outcome = market.outcomes[isOver ? 'Over' : 'Under'];
          }

          if (outcome && outcome.best_link) {
            matches[bookmakerKey] = {
              ...BOOKMAKERS[bookmakerKey],
              key: bookmakerKey,
              event,
              market,
              outcome,
              link: outcome.best_link,
              price: outcome.price,
              point: outcome.point
            };
          }
        }
      });
    }
  });

  return matches;
}

/**
 * Create a parlay link for multiple selections
 * @param {string} bookmakerKey - The bookmaker to create parlay for
 * @param {Array} selections - Array of bet selections
 * @returns {string|null} Parlay deep link URL
 */
export function createParlayLink(bookmakerKey, selections) {
  if (bookmakerKey === 'fanduel') {
    // FanDuel supports multiple legs in one deep link
    const base = 'https://sportsbook.fanduel.com/addToBetslip';
    const params = [];

    selections.forEach((selection, i) => {
      // Try multiple ways to extract market and selection IDs
      const marketId = selection.hierarchy?.marketId ||
                       selection.hierarchy?.market?.sid ||
                       selection.hierarchy?.sids?.market ||
                       selection.market?.sid;

      const selectionId = selection.hierarchy?.selectionId ||
                         selection.hierarchy?.outcome?.sid ||
                         selection.hierarchy?.sids?.outcome ||
                         selection.outcome?.sid;

      if (marketId && selectionId) {
        params.push(`marketId[${i}]=${marketId}`);
        params.push(`selectionId[${i}]=${selectionId}`);
      }
    });

    return params.length > 0 ? `${base}?${params.join('&')}` : null;
  }

  // Add support for other bookmakers as needed
  if (bookmakerKey === 'draftkings') {
    // DraftKings parlay implementation
    // Note: Their URL structure might be different
  }

  return null;
}

/**
 * Fetch player props for a specific event
 * @param {string} sport - Sport key (e.g., 'basketball_nba')
 * @param {string} eventId - Event ID from the odds API
 * @param {Object} options - Additional options
 * @returns {Promise<Object>} Player props data with deep links
 */
export async function fetchEventPlayerProps(sport, eventId, options = {}) {
  const {
    bookmakers = Object.keys(BOOKMAKERS),
    markets = 'player_points,player_rebounds,player_assists',
    oddsFormat = 'american',
    regions = 'us'
  } = options;

  if (!ODDS_API_KEY) {
    throw new Error('Odds API key is not configured');
  }

  const params = new URLSearchParams({
    apiKey: ODDS_API_KEY,
    bookmakers: bookmakers.join(','),
    markets,
    oddsFormat,
    regions,
    includeLinks: 'true',
    includeSids: 'true'
  });

  try {
    const response = await fetch(`${BASE_URL}/sports/${sport}/events/${eventId}/odds?${params}`);

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();

    // Log the RAW player props response to understand structure
    console.group(`🔍 RAW Player Props Response for event ${eventId}`);
    console.log('Full response:', data);
    console.log('Bookmakers:', data.bookmakers);
    if (data.bookmakers && data.bookmakers.length > 0) {
      console.log('First bookmaker:', data.bookmakers[0]);
      console.log('First bookmaker markets:', data.bookmakers[0].markets);
      if (data.bookmakers[0].markets && data.bookmakers[0].markets.length > 0) {
        console.log('First market:', data.bookmakers[0].markets[0]);
        console.log('First market outcomes:', data.bookmakers[0].markets[0].outcomes);
      }
    }
    console.groupEnd();

    return processOddsData([data])[0]; // Process single event
  } catch (error) {
    console.error(`Error fetching player props for event ${eventId}:`, error);
    throw error;
  }
}

/**
 * Fetch player props for all events in a sport
 * @param {Array} events - Array of event objects with id field
 * @param {string} sport - Sport key
 * @param {Object} options - Additional options
 * @returns {Promise<Array>} Array of events with player props
 */
export async function fetchPlayerPropsForEvents(events, sport, options = {}) {
  console.log('📊 Fetching player props for', events.length, 'events...');

  const playerPropsPromises = events.map(async (event) => {
    try {
      const playerProps = await fetchEventPlayerProps(sport, event.id, options);

      // Log if player props are empty
      const bookmakerCount = Object.keys(playerProps.bookmakers || {}).length;
      if (bookmakerCount === 0) {
        console.warn(`⚠️ No player props available for ${event.away_team} @ ${event.home_team} (eventId: ${event.id})`);
      } else {
        console.log(`✅ Loaded player props for ${event.away_team} @ ${event.home_team} (${bookmakerCount} bookmakers)`);
      }

      return { ...event, playerProps };
    } catch (error) {
      console.warn(`❌ Failed to fetch player props for event ${event.id}:`, error.message);
      return { ...event, playerProps: null };
    }
  });

  const results = await Promise.all(playerPropsPromises);

  // Summary
  const withProps = results.filter(e => e.playerProps && Object.keys(e.playerProps.bookmakers || {}).length > 0).length;
  const withoutProps = results.filter(e => !e.playerProps || Object.keys(e.playerProps.bookmakers || {}).length === 0).length;
  console.log(`📊 Player props summary: ${withProps} games with props, ${withoutProps} games without props`);

  return results;
}

/**
 * Get available sports from the Odds API
 */
export async function fetchAvailableSports() {
  try {
    const response = await fetch(`${BASE_URL}/sports?apiKey=${ODDS_API_KEY}`);

    if (!response.ok) {
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching sports:', error);
    throw error;
  }
}